import ProductsActionCreators from "@/Actions/Products";
import { checkCanMultiplePricingPlansFor } from "@/Components/Utils";
import useDispatch from "@/Components/Utils/useDispatch";
import { ZomentumRoutes } from "@/Contracts";
import {
	AddOrEditBundleRequest,
	AddOrEditProductRequest,
	BundleProduct,
	Product,
	ProductBillingType,
	ProductFormValues,
	ProductPricingPlan,
	ProductSource,
	ProductType,
	BundleSettingDetail
} from "@/Contracts/Products";
import {
	Distributor,
	PricingOptions,
	PricingRadioOptions,
	ProductMiscEnums
} from "@/Contracts/Products/index";
import { CustomField } from "@/Contracts/Settings";
import ZMessage from "@molecules/ZMessage";

import { processCustomFieldFormValues } from "@/Utils/common";
import { useHistory } from "react-router-dom";

const useAddProduct = (
	productPricingPlanList: ProductPricingPlan[],
	bundledProductList: BundleProduct[],
	productImageList: string[],
	customFieldList: CustomField[],
	resetState: () => void,
	selectedEtilizeProduct: Product | null,
	selectedDistributor: PricingRadioOptions | null,
	selectedPricing: ProductPricingPlan | PricingOptions | null,
	shouldRedirectToProduct?: boolean,
	onAdd?: (product: Product) => void,
	bundleSettingDetail?: BundleSettingDetail,
	isBundleEnhancementEnable?: boolean
): ((addProductFormValues: ProductFormValues) => void) => {
	const dispatch = useDispatch();
	const history = useHistory();

	const onAddSubmit = (addProductFormValues: ProductFormValues) => {
		const canHaveMultiplePricingPlans = checkCanMultiplePricingPlansFor(
			addProductFormValues.type
		);
		if (
			canHaveMultiplePricingPlans &&
			!productPricingPlanList?.length &&
			!selectedEtilizeProduct
		) {
			ZMessage.error("Please add atleast 1 pricing plan");
			return;
		}

		if (
			addProductFormValues.type === ProductType.Bundle &&
			!bundledProductList?.length
		) {
			ZMessage.error("Please add atleast 1 product to bundle");
			return;
		}

		if (
			!canHaveMultiplePricingPlans ||
			(!!selectedEtilizeProduct &&
				addProductFormValues.type === ProductType.Hardware)
		) {
			if (
				addProductFormValues.type === ProductType.Bundle &&
				addProductFormValues.show_individual_product_pricing
			) {
				const productPricingPlan = new ProductPricingPlan();
				productPricingPlan.setup_price =
					addProductFormValues.selected_setup_price || 0;
				addProductFormValues.pricing = [productPricingPlan];
			} else {
				// for hardware or bundled items with parent level pricing
				const productPricingPlan = new ProductPricingPlan();
				productPricingPlan.name = ProductMiscEnums.DefaultPricing;
				productPricingPlan.billing_period =
					addProductFormValues.billing_period ||
					ProductBillingType.OneTime;
				productPricingPlan.cost_price =
					addProductFormValues.selected_cost_price || 0;
				productPricingPlan.sell_price =
					addProductFormValues.sell_price || 0;
				productPricingPlan.setup_price =
					addProductFormValues.selected_setup_price || 0;
				productPricingPlan.supplier_id =
					addProductFormValues.supplier_id;
				addProductFormValues.pricing = [productPricingPlan];
				productPricingPlan.vendor_name =
					addProductFormValues.vendor_name;
				productPricingPlan.vendor_sku_no =
					addProductFormValues.vendor_sku_no;
			}
		} else {
			addProductFormValues.pricing = productPricingPlanList || [];
		}

		if (addProductFormValues.type === ProductType.Bundle) {
			addProductFormValues.bundled_items = [...bundledProductList];
		}

		addProductFormValues.item_images =
			Array.from(new Set(productImageList)) || new Array<string>();

		addProduct(addProductFormValues);
	};

	const addProduct = async (addProductFormValues: ProductFormValues) => {
		dispatch(ProductsActionCreators.showProductsLoader());
		try {
			let addProductRequest = new AddOrEditProductRequest();
			addProductRequest.name = addProductFormValues.name;
			addProductRequest.description = addProductFormValues.description;
			addProductRequest.type = addProductFormValues.type;
			addProductRequest.product_category =
				addProductFormValues.product_category;
			addProductRequest.manufacturer = addProductFormValues.manufacturer;
			addProductRequest.manufacturer_part_no =
				addProductFormValues.manufacturer_part_no;
			addProductRequest.item_images = addProductFormValues.item_images;
			addProductRequest.vendor_id = addProductFormValues.vendor_id;
			addProductRequest.tax_category_id =
				addProductFormValues.tax_category_id;
			addProductRequest.show_individual_product_pricing =
				isBundleEnhancementEnable
					? bundleSettingDetail?.showPrice
					: addProductFormValues.show_individual_product_pricing;
			addProductRequest.should_add_setup_price_to_margin =
				addProductFormValues.should_add_setup_price_to_margin;
			addProductRequest.unit_of_measure =
				addProductFormValues.unit_of_measure;
			addProductRequest.mandatory_custom_field_check_require = true;
			addProductRequest.supplier_id = addProductFormValues.supplier_id;
			addProductRequest.pricing = addProductFormValues.pricing.map(
				pricing => {
					pricing.cost_price = Number(pricing.cost_price || 0);
					pricing.sell_price = Number(pricing.sell_price || 0);
					pricing.setup_price = Number(pricing.setup_price || 0);
					pricing.margin = Number(pricing.margin || 0);
					pricing.markup = Number(pricing.markup || 0);
					pricing.billing_period =
						addProductFormValues.selected_billing_period ??
						pricing.billing_period ??
						ProductBillingType.OneTime;
					return pricing;
				}
			);

			if (isBundleEnhancementEnable) {
				addProductRequest.show_child_quantity =
					bundleSettingDetail?.showQuantity;
				addProductRequest.show_child_items_in_quote_block =
					bundleSettingDetail?.showChildItems;
			}
			addProductRequest.asin = addProductFormValues.asin;
			addProductRequest.selected_pricing_distributor =
				selectedDistributor !== "custom" ? selectedDistributor : null;

			if (selectedDistributor === Distributor.CustomDistributor) {
				const tempSelectedPricing =
					selectedPricing as ProductPricingPlan;
				addProductRequest.selected_custom_distributor_details = {
					custom_distributor_id:
						tempSelectedPricing.distributor_pricing_details
							?.custom_distributor_pricing_details
							?.custom_distributor.id || "",
					selected_custom_distributor_pricing_id:
						tempSelectedPricing.distributor_pricing_details
							?.custom_distributor_pricing_details
							?.custom_distributor_pricing_id || "",
					custom_fields:
						tempSelectedPricing?.distributor_pricing_details
							?.custom_distributor_pricing_details
							?.custom_fields ?? []
				};
			}
			addProductRequest.bundled_items = addProductFormValues.bundled_items
				? addProductFormValues.bundled_items.map(bundledProduct => {
						const newBundledProduct: AddOrEditBundleRequest = {
							...bundledProduct
						};
						delete newBundledProduct.item;
						return newBundledProduct;
				  })
				: undefined;
			addProductRequest.api_sync_metadata =
				selectedEtilizeProduct?.sync_data &&
				selectedEtilizeProduct.product_source !== ProductSource.Self
					? {
							sync_source:
								selectedEtilizeProduct.sync_data[0].synced_from,
							sync_source_id: selectedEtilizeProduct.id
					  }
					: undefined;
			const customFieldFormValues =
				addProductFormValues.custom_field_form_values;
			if (customFieldFormValues && customFieldList?.length > 0) {
				addProductRequest = {
					...addProductRequest,
					custom_fields: processCustomFieldFormValues(
						customFieldFormValues,
						customFieldList
					)
				};
			}
			const addProductResponse = await dispatch(
				ProductsActionCreators.addProduct(addProductRequest)
			);
			if (onAdd) {
				resetState();
				onAdd(addProductResponse.addProduct);
			}
			selectedEtilizeProduct &&
				dispatch(ProductsActionCreators.resetSelectedEtilizeProduct());
			shouldRedirectToProduct &&
				dispatch(ProductsActionCreators.addProductDrawerClosed());
			ZMessage.success("Product added successfully");
			if (
				shouldRedirectToProduct &&
				!!addProductResponse?.addProduct?.id?.length
			) {
				dispatch(ProductsActionCreators.hideProductsLoader());
				dispatch(
					ProductsActionCreators.addEtilizeProductDrawerClosed()
				);
				history.push(
					ZomentumRoutes.ProductsDetail.replace(
						":id",
						addProductResponse.addProduct.id
					)
				);
			}
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(ProductsActionCreators.hideProductsLoader());
		}
	};

	return onAddSubmit;
};

export default useAddProduct;
